// src/app/demo/page.tsx
"use client";

import React, { useState, useEffect, useCallback } from 'react';
import SnapSiteForm from './SnapSiteForm';
import UserProfilePage from '@/components/snapsite/UserProfilePage';
import type { SnapSiteData } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Info, ChevronDown } from 'lucide-react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Perfil estático de showcase — realista, completamente lleno
const showcaseProfile: SnapSiteData = {
  ownerUID: 'showcase-uid',
  ownerEmail: 'dra.sofia@ejemplo.com',
  username: 'dra-sofia-luna',
  name: 'Dra. Sofía Luna',
  bio: 'Médica general con 12 años de experiencia. Consultas presenciales y en línea. Especialista en medicina preventiva y nutrición clínica.',
  profileImageStoragePath: null,
  profileImageUrl: 'https://placehold.co/300x300/7c3aed/ffffff?text=SL&font=raleway',
  socialLinks: {
    website: 'https://ejemplo.com',
    instagram: 'https://instagram.com/ejemplo',
    whatsapp: '+5215512345678',
    facebook: 'https://facebook.com/ejemplo',
    twitter: '',
    youtube: '',
    tiktok: '',
  },
  personalLinks: [
    { name: 'Agendar consulta en línea', url: 'https://ejemplo.com/agendar' },
    { name: 'Ver mis artículos de salud', url: 'https://ejemplo.com/blog' },
  ],
  copyableBlocks: [
    {
      id: 'showcase-block-1',
      icon: 'bank',
      title: 'Datos para transferencia',
      fields: [
        { name: 'CLABE', value: '012345678901234567' },
        { name: 'Banco', value: 'BBVA' },
        { name: 'Titular', value: 'Sofía Luna Martínez' },
      ],
    },
  ],
  last_updated: new Date().toISOString(),
  status: 'published',
  phone: '+52 55 1234 5678',
  company: 'Clínica Bienestar',
  position: 'Médica General',
  location: 'Ciudad de México',
  isPremium: true,
  theme: 'default',
  accentColor: 'violet',
  primaryColor: 'default',
  layout: 'minimal',
  animateIcons: true,
  whatsappShareEnabled: true,
};

// Datos iniciales para el editor interactivo
const initialDemoData: SnapSiteData = {
  ownerUID: 'demo-user-uid',
  ownerEmail: 'demo@example.com',
  username: 'tu-demo-usuario',
  name: 'Tu Nombre (Demo)',
  bio: '¡Hola! Esta es una demostración de mi SnapSite. Puedes editar esto y ver cómo se ve en tiempo real.',
  profileImageStoragePath: null,
  profileImageUrl: 'https://placehold.co/300x300/A090D0/FFFFFF.png?text=Demo&font=roboto',
  socialLinks: {
    website: 'https://ejemplo.com',
    twitter: 'https://x.com/ejemplo',
    instagram: 'https://instagram.com/ejemplo',
    facebook: '',
    youtube: '',
    tiktok: '',
    whatsapp: '',
  },
  personalLinks: [
    { name: 'Mi Proyecto Demo', url: 'https://proyecto-demo.com' },
  ],
  copyableBlocks: [
    {
      id: 'demo-block-1',
      icon: 'bank',
      title: 'Datos de Prueba (Demo)',
      fields: [
        { name: 'CLABE', value: '123456789012345678' },
        { name: 'Banco', value: 'Banco de Demos' },
      ],
    },
  ],
  last_updated: new Date().toISOString(),
  status: 'published',
  phone: '+1 234 567 8900',
  company: 'Demo Corp',
  position: 'Explorador de Demos',
  location: 'Ciudad Demo, Internet',
  isPremium: false,
  theme: 'default',
  layout: 'minimal',
};

export default function DemoPage() {
  const [demoData, setDemoData] = useState<SnapSiteData>(initialDemoData);
  const [isPremiumView, setIsPremiumView] = useState<boolean>(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Los cambios en la demo se perderán si abandonas la página.';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleDemoFormUpdate = useCallback((updatedData: SnapSiteData) => {
    setDemoData(prevData => ({
      ...prevData,
      ...updatedData,
      isPremium: isPremiumView,
      theme: isPremiumView ? updatedData.theme : 'default',
    }));
  }, [isPremiumView]);

  const handlePremiumSwitch = (checked: boolean) => {
    setIsPremiumView(checked);
    setDemoData(prev => ({
      ...prev,
      isPremium: checked,
      theme: checked ? prev.theme : 'default',
    }));
  };

  if (!isClient) return null;

  return (
    <div className="min-h-screen">

      {/* ── Sección 1: Showcase estático ───────────────────────────────────── */}
      <section className="bg-gradient-to-b from-background to-muted/30 py-16 px-4">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">Ejemplo real</p>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Así se ve tu tarjeta digital
          </h1>
          <p className="text-muted-foreground">
            Un perfil completo, con foto, redes, links y datos de contacto — todo en una sola página.
          </p>
        </div>

        {/* Perfil estático renderizado con MinimalLayout */}
        <div className="max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl border border-border/50">
          <UserProfilePage data={showcaseProfile} />
        </div>

        {/* Flecha hacia abajo */}
        <div className="flex flex-col items-center mt-10 gap-1 text-muted-foreground">
          <p className="text-sm">Ahora pruébalo tú</p>
          <ChevronDown className="h-5 w-5 animate-bounce" />
        </div>
      </section>

      <Separator />

      {/* ── Sección 2: Editor interactivo ──────────────────────────────────── */}
      <section className="container mx-auto px-2 py-10">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">Editor interactivo</h2>
          <p className="text-muted-foreground text-sm">
            Modifica los datos y ve cómo queda tu perfil en tiempo real. Sin cuenta, sin guardar.
          </p>
        </div>

        <Card className="mb-6 shadow-sm bg-card">
          <CardContent className="pt-5">
            <Alert variant="destructive" className="mb-5 bg-destructive/10 border-destructive/30 text-destructive dark:text-destructive-foreground dark:bg-destructive/20 dark:border-destructive/50">
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle className="font-semibold">Modo Demo</AlertTitle>
              <AlertDescription>
                Ningún dato se guarda. Los cambios se pierden al cerrar esta página.
              </AlertDescription>
            </Alert>

            <div className="flex items-center gap-3 p-4 border rounded-md bg-muted/50">
              <Switch
                id="premium-mode-switch"
                checked={isPremiumView}
                onCheckedChange={handlePremiumSwitch}
                aria-label="Simular vista premium"
              />
              <Label htmlFor="premium-mode-switch" className="text-base font-medium">
                Simular vista {isPremiumView ? 'Premium ✨' : 'Gratuita'}
              </Label>
              <p className="text-xs text-muted-foreground ml-auto hidden sm:block">
                Activa para desbloquear temas y colores
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="order-2 lg:order-1">
            <h3 className="text-lg font-semibold mb-4 text-center text-foreground">Editor</h3>
            <SnapSiteForm
              initialData={demoData}
              isDemoMode={true}
              onDemoFormUpdate={handleDemoFormUpdate}
              demoInitialPremiumState={isPremiumView}
            />
          </div>

          <div className="order-1 lg:order-2">
            <h3 className="text-lg font-semibold mb-4 text-center text-foreground">Vista previa</h3>
            <Card className="sticky top-8 shadow-md overflow-hidden">
              <CardContent className="p-0">
                <div className="max-h-[calc(100vh-6rem)] overflow-y-auto">
                  {demoData ? (
                    <UserProfilePage data={demoData} />
                  ) : (
                    <p className="p-8 text-center text-muted-foreground">Cargando vista previa...</p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="p-3 bg-muted/30 border-t">
                <p className="text-xs text-muted-foreground text-center w-full">
                  La vista previa se actualiza al hacer clic en "Actualizar Vista Previa Demo".
                </p>
              </CardFooter>
            </Card>
          </div>
        </div>

        <div className="mt-14 text-center">
          <NextImage
            src="/devmonkeys/devmonkeys_listos.webp"
            alt="Monos desarrolladores esperando para crear tu SnapSite real"
            width={250}
            height={150}
            className="mx-auto mb-4 rounded-lg"
            data-ai-hint="monkeys ready"
            unoptimized
          />
          <p className="text-muted-foreground mb-4">¿Te gustó la demo?</p>
          <NextLink href="/" className={cn(buttonVariants({ size: "lg" }), "bg-primary hover:bg-primary/90 text-primary-foreground")}>
            ¡Crea tu SnapSite Real Ahora!
          </NextLink>
        </div>
      </section>
    </div>
  );
}
